/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Platform Settings — Branding, Landing CMS, Login CMS
 * Accessible to Platform Owner / Super Admin only.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Save, CheckCircle, Image as ImageIcon, Trash2, Plus, X,
  Upload, Globe, LogIn, Palette, ToggleRight, ToggleLeft,
  AlertTriangle, Newspaper, Sparkles, Activity, Cpu, BarChart3,
  Loader2, RefreshCw, Zap, Building2, Eye, EyeOff, Plug,
} from 'lucide-react';
import { PlatformSettings } from '../domain/entities';
import { getRepository } from '../config/repository';
import { isRemoteMode } from '../config/repository';
import { getAuthToken } from '../data/ApiModuleRepository';
import { cn } from '../ui/cn';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

const repo = () => getRepository();

// ── Image Upload Helper ─────────────────────────────────────────────────────

function ImagePicker({
  value,
  onChange,
  purpose,
  label,
  hint,
  accept = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/x-icon',
}: {
  value?: string;
  onChange: (url: string | undefined) => void;
  purpose: string;
  label: string;
  hint?: string;
  accept?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (file: File) => {
    if (!isRemoteMode()) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', purpose);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/media/platform-upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json() as { key: string; url: string };
      // Use the public media URL so landing/login pages can load it without auth
      onChange(`/api/public-media/${data.key}`);
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const previewUrl = value
    ? (value.startsWith('http') ? value : `${API_BASE}${value}`)
    : undefined;

  return (
    <div className="space-y-2">
      <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest">{label}</label>
      {hint && <p className="text-[11px] text-zinc-400">{hint}</p>}
      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className={cn(
          "w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 transition-colors",
          value ? "border-zinc-300 bg-white" : "border-zinc-200 bg-zinc-50"
        )}>
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="w-full h-full object-contain" />
          ) : (
            <ImageIcon size={24} className="text-zinc-300" />
          )}
        </div>
        {/* Actions */}
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900 text-white rounded-lg text-xs font-bold hover:bg-zinc-800 transition-all disabled:opacity-50"
          >
            <Upload size={12} /> {uploading ? 'Uploading…' : 'Upload Image'}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="flex items-center gap-1 px-3 py-1.5 text-red-500 text-xs font-bold hover:text-red-700 transition-colors"
            >
              <Trash2 size={11} /> Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── AI Provider + Model Options ─────────────────────────────────────────────

type AiProvider = 'gemini' | 'openai' | 'anthropic' | 'groq';

interface ProviderDef {
  id: AiProvider;
  label: string;
  description: string;
  color: string;
  models: { id: string; label: string; desc: string; tier: string }[];
}

const AI_PROVIDERS: ProviderDef[] = [
  {
    id: 'gemini', label: 'Google Gemini', description: 'Free tier available, great for F&B translation', color: 'blue',
    models: [
      { id: 'gemini-2.0-flash',       label: 'Gemini 2.0 Flash',       desc: 'Fast, 1,500 RPD free tier',    tier: 'Recommended' },
      { id: 'gemini-2.0-flash-lite',  label: 'Gemini 2.0 Flash-Lite', desc: 'Fastest, 3,000 RPD free tier', tier: 'Budget' },
      { id: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash',       desc: 'Latest, 20 RPD free tier',     tier: 'Advanced' },
      { id: 'gemini-1.5-flash',       label: 'Gemini 1.5 Flash',       desc: 'Stable, 1,500 RPD free tier',  tier: 'Legacy' },
    ],
  },
  {
    id: 'openai', label: 'OpenAI', description: 'GPT-4o and GPT-4o-mini, paid API', color: 'emerald',
    models: [
      { id: 'gpt-4o-mini',       label: 'GPT-4o Mini',       desc: 'Fast & cheap, great for translation',    tier: 'Recommended' },
      { id: 'gpt-4o',            label: 'GPT-4o',            desc: 'Most capable, higher cost per token',    tier: 'Advanced' },
      { id: 'gpt-4.1-mini',     label: 'GPT-4.1 Mini',     desc: 'Latest mini model',                      tier: 'Budget' },
      { id: 'gpt-3.5-turbo',    label: 'GPT-3.5 Turbo',    desc: 'Legacy, lowest cost',                    tier: 'Legacy' },
    ],
  },
  {
    id: 'anthropic', label: 'Anthropic Claude', description: 'Claude models, paid API', color: 'amber',
    models: [
      { id: 'claude-sonnet-4-20250514',   label: 'Claude Sonnet 4',   desc: 'Fast & capable, good value',     tier: 'Recommended' },
      { id: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku', desc: 'Fastest Claude, lowest cost',     tier: 'Budget' },
      { id: 'claude-opus-4-20250514',     label: 'Claude Opus 4',     desc: 'Most capable, highest cost',     tier: 'Advanced' },
    ],
  },
  {
    id: 'groq', label: 'Groq', description: 'Ultra-fast inference, free tier available', color: 'violet',
    models: [
      { id: 'llama-3.3-70b-versatile',  label: 'Llama 3.3 70B',     desc: 'Strong open model, free tier',     tier: 'Recommended' },
      { id: 'llama-3.1-8b-instant',     label: 'Llama 3.1 8B',      desc: 'Ultra fast, lower quality',        tier: 'Budget' },
      { id: 'gemma2-9b-it',             label: 'Gemma 2 9B',        desc: 'Google open model on Groq',        tier: 'Budget' },
      { id: 'mixtral-8x7b-32768',       label: 'Mixtral 8x7B',      desc: 'Good balance, 32K context',        tier: 'Advanced' },
    ],
  },
];

// ── AI Usage Types ──────────────────────────────────────────────────────────

interface AiUsageData {
  today: { requests: number; characters: number };
  allTime: { requests: number; characters: number };
  byOrg: { orgId: string; orgName: string; requests: number; characters: number }[];
}

// ── AI Engine Tab Component ─────────────────────────────────────────────────

function AiEngineTab({ settings, update }: { settings: PlatformSettings; update: (p: Partial<PlatformSettings>) => void }) {
  const [usage, setUsage] = useState<AiUsageData | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  // Track the new key the user is typing — empty means "don't change stored key"
  // Track new key typed by user — empty means "don't change stored key"
  const [newApiKey, setNewApiKey] = useState('');
  // Whether user clicked "Change Key" to show input
  const [editingKey, setEditingKey] = useState(false);
  // Whether a key exists on the server (boolean from API, never contains actual key)
  const hasStoredKey = !!settings.aiApiKeyConfigured;

  const fetchUsage = async () => {
    if (!isRemoteMode()) return;
    setLoadingUsage(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/system/ai-usage`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const json = await res.json() as { data: AiUsageData };
        setUsage(json.data);
      }
    } catch { /* ignore */ }
    finally { setLoadingUsage(false); }
  };

  useEffect(() => { fetchUsage(); }, []);

  const currentProvider = (settings.aiProvider || 'gemini') as AiProvider;
  const providerDef = AI_PROVIDERS.find(p => p.id === currentProvider) ?? AI_PROVIDERS[0];
  const currentModel = settings.aiModel || providerDef.models[0]?.id || '';

  // When provider changes, auto-select the first (recommended) model of the new provider
  const handleProviderChange = (providerId: AiProvider) => {
    const prov = AI_PROVIDERS.find(p => p.id === providerId) ?? AI_PROVIDERS[0];
    update({ aiProvider: providerId, aiModel: prov.models[0]?.id });
    setTestResult(null);
    setNewApiKey('');
    setEditingKey(false);
  };

  const handleTestConnection = async () => {
    if (!isRemoteMode()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE}/api/system/ai-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          provider: currentProvider,
          model: currentModel,
          apiKey: newApiKey || undefined,
        }),
      });
      const json = await res.json() as { ok: boolean; provider?: string; model?: string; error?: string; response?: string };
      if (json.ok) {
        setTestResult({ ok: true, message: `Connected to ${json.provider}/${json.model}` });
      } else {
        setTestResult({ ok: false, message: json.error || 'Connection failed' });
      }
    } catch (e: any) {
      setTestResult({ ok: false, message: e.message || 'Network error' });
    } finally { setTesting(false); }
  };

  const providerColorMap: Record<string, string> = {
    blue:    'border-blue-300 bg-blue-50 ring-blue-200',
    emerald: 'border-emerald-300 bg-emerald-50 ring-emerald-200',
    amber:   'border-amber-300 bg-amber-50 ring-amber-200',
    violet:  'border-violet-300 bg-violet-50 ring-violet-200',
  };
  const providerDotMap: Record<string, string> = {
    blue: 'border-blue-500', emerald: 'border-emerald-500', amber: 'border-amber-500', violet: 'border-violet-500',
  };
  const providerDotFillMap: Record<string, string> = {
    blue: 'bg-blue-500', emerald: 'bg-emerald-500', amber: 'bg-amber-500', violet: 'bg-violet-500',
  };

  return (
    <div className="space-y-5">

      {/* ── Master Toggle ──────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-zinc-900 flex items-center gap-1.5">
              <Sparkles size={14} className="text-violet-500" /> Enable AI Features
            </p>
            <p className="text-xs text-zinc-400 mt-0.5">When OFF, the ✨ AI translation magic wand is hidden platform-wide for all organisations.</p>
          </div>
          <button
            onClick={() => update({ aiFeaturesEnabled: !settings.aiFeaturesEnabled })}
            className="transition-colors"
          >
            {settings.aiFeaturesEnabled
              ? <ToggleRight size={32} className="text-emerald-500" />
              : <ToggleLeft size={32} className="text-zinc-400" />
            }
          </button>
        </div>
      </div>

      {settings.aiFeaturesEnabled && (
        <>
          {/* ── Provider Selector ────────────────────────────────── */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
              <Plug size={16} className="text-blue-500" /> AI Provider
            </div>
            <p className="text-xs text-zinc-400">Select which AI provider to use for all translations. Each provider requires its own API key.</p>

            <div className="grid grid-cols-2 gap-2">
              {AI_PROVIDERS.map(p => {
                const selected = p.id === currentProvider;
                return (
                  <button key={p.id} type="button" onClick={() => handleProviderChange(p.id)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all",
                      selected
                        ? `${providerColorMap[p.color] ?? ''} ring-1`
                        : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50"
                    )}>
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                      selected ? (providerDotMap[p.color] ?? 'border-blue-500') : "border-zinc-300"
                    )}>
                      {selected && <div className={cn("w-2 h-2 rounded-full", providerDotFillMap[p.color] ?? 'bg-blue-500')} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-bold text-zinc-900">{p.label}</span>
                      <p className="text-[11px] text-zinc-400 mt-0.5">{p.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── API Key ──────────────────────────────────────────── */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
              <AlertTriangle size={16} className="text-amber-500" /> API Key
            </div>
            <p className="text-xs text-zinc-400">
              Your {providerDef.label} API key is stored securely and never shown again after saving.
              {currentProvider === 'gemini' && ' Falls back to the GOOGLE_AI_KEY worker secret if left blank.'}
            </p>

            {/* Key status + actions */}
            {hasStoredKey && !editingKey && !newApiKey ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-700">
                    <CheckCircle size={12} /> API key configured
                  </span>
                  <button type="button" onClick={() => setEditingKey(true)}
                    className="text-xs font-bold text-blue-500 hover:text-blue-700 transition-colors">
                    Change key
                  </button>
                  <button type="button" onClick={() => { update({ aiApiKey: '', aiApiKeyConfigured: false }); setNewApiKey(''); setEditingKey(true); }}
                    className="text-xs font-bold text-red-400 hover:text-red-600 transition-colors">
                    Remove key
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={handleTestConnection} disabled={testing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-zinc-900 hover:bg-zinc-800 rounded-lg transition-all disabled:opacity-50">
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    {testing ? 'Testing…' : 'Test Connection'}
                  </button>
                  {testResult && (
                    <span className={cn("text-xs font-medium", testResult.ok ? "text-emerald-600" : "text-red-500")}>
                      {testResult.ok ? '✓' : '✗'} {testResult.message}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {!hasStoredKey && !newApiKey && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 border border-amber-200 text-xs font-bold text-amber-700">
                    <AlertTriangle size={12} /> No key set
                  </span>
                )}
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={newApiKey}
                    onChange={e => {
                      setNewApiKey(e.target.value);
                      update({ aiApiKey: e.target.value || undefined });
                    }}
                    placeholder={`Paste ${providerDef.label} API key…`}
                    autoComplete="off"
                    data-1p-ignore
                    className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 pr-20 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                  <button type="button" onClick={() => setShowKey(!showKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-400 hover:text-zinc-600 transition-colors">
                    {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {newApiKey && (
                  <p className="text-[10px] text-amber-500 font-medium flex items-center gap-1">
                    <AlertTriangle size={10} /> New key entered — click "Save All Settings" to apply.
                  </p>
                )}
                <div className="flex items-center gap-2">
                  {hasStoredKey && editingKey && (
                    <button type="button" onClick={() => { setEditingKey(false); setNewApiKey(''); update({ aiApiKey: undefined }); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-zinc-500 hover:text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-all">
                      Cancel
                    </button>
                  )}
                  <button type="button" onClick={handleTestConnection} disabled={testing || !newApiKey}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-zinc-900 hover:bg-zinc-800 rounded-lg transition-all disabled:opacity-50">
                    {testing ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    {testing ? 'Testing…' : 'Test Connection'}
                  </button>
                  {testResult && (
                    <span className={cn("text-xs font-medium", testResult.ok ? "text-emerald-600" : "text-red-500")}>
                      {testResult.ok ? '✓' : '✗'} {testResult.message}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Usage Dashboard ──────────────────────────────────── */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
                <BarChart3 size={16} className="text-emerald-500" /> AI Usage Dashboard
              </div>
              <button onClick={fetchUsage} disabled={loadingUsage}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold text-zinc-500 hover:text-zinc-700 bg-zinc-50 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50">
                <RefreshCw size={11} className={loadingUsage ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>

            {!usage && !loadingUsage && (
              <p className="text-xs text-zinc-400 py-4 text-center">No usage data available — connect to remote API to see stats.</p>
            )}

            {loadingUsage && !usage && (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-zinc-300" />
              </div>
            )}

            {usage && (
              <>
                {/* Stat Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Today Requests" value={usage.today.requests} icon={<Zap size={14} className="text-amber-500" />} />
                  <StatCard label="Today Chars" value={usage.today.characters.toLocaleString()} icon={<BarChart3 size={14} className="text-blue-500" />} />
                  <StatCard label="All-Time Requests" value={usage.allTime.requests.toLocaleString()} icon={<Zap size={14} className="text-zinc-400" />} />
                  <StatCard label="All-Time Chars" value={usage.allTime.characters.toLocaleString()} icon={<BarChart3 size={14} className="text-zinc-400" />} />
                </div>

                {/* Per-Org Table */}
                {usage.byOrg.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-2">Usage by Organisation (Today)</p>
                    <div className="border border-zinc-100 rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-zinc-50 text-zinc-500">
                            <th className="text-left px-3 py-2 font-bold">Organisation</th>
                            <th className="text-right px-3 py-2 font-bold">Requests</th>
                            <th className="text-right px-3 py-2 font-bold">Characters</th>
                          </tr>
                        </thead>
                        <tbody>
                          {usage.byOrg.map(org => (
                            <tr key={org.orgId} className="border-t border-zinc-50 hover:bg-zinc-50/50">
                              <td className="px-3 py-2 font-medium text-zinc-700 flex items-center gap-1.5">
                                <Building2 size={12} className="text-zinc-300" /> {org.orgName}
                              </td>
                              <td className="text-right px-3 py-2 text-zinc-600 font-mono">{org.requests}</td>
                              <td className="text-right px-3 py-2 text-zinc-600 font-mono">{org.characters.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Model Selector ───────────────────────────────────── */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
              <Cpu size={16} className="text-violet-500" /> AI Model
            </div>
            <p className="text-xs text-zinc-400">Select the {providerDef.label} model used for all AI translations.</p>

            <div className="grid gap-2">
              {providerDef.models.map(m => {
                const selected = m.id === currentModel;
                return (
                  <button key={m.id} type="button" onClick={() => update({ aiModel: m.id })}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all",
                      selected
                        ? "border-violet-300 bg-violet-50 ring-1 ring-violet-200"
                        : "border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50"
                    )}>
                    <div className={cn(
                      "w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                      selected ? "border-violet-500" : "border-zinc-300"
                    )}>
                      {selected && <div className="w-2 h-2 rounded-full bg-violet-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-zinc-900">{m.label}</span>
                        <span className={cn(
                          "text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                          m.tier === 'Recommended' ? 'bg-emerald-100 text-emerald-700' :
                          m.tier === 'Budget' ? 'bg-blue-100 text-blue-700' :
                          m.tier === 'Advanced' ? 'bg-violet-100 text-violet-700' :
                          'bg-zinc-100 text-zinc-500'
                        )}>{m.tier}</span>
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-0.5">{m.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-zinc-400 flex items-center gap-1">
              <AlertTriangle size={10} className="text-amber-400" /> Changing models takes effect immediately after saving.
            </p>
          </div>

          {/* ── Persona Override ──────────────────────────────────── */}
          <div className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-3">
            <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
              <Sparkles size={16} className="text-amber-500" /> AI Translation Persona
            </div>
            <textarea
              value={settings.aiTranslationPersona ?? ''}
              onChange={e => update({ aiTranslationPersona: e.target.value || undefined })}
              placeholder="Leave blank to use the built-in F&B specialist prompt. Override here to customise the AI system instruction for all translations…"
              rows={6}
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-500 resize-y"
            />
            <p className="text-xs text-zinc-400">Custom system instruction sent to {providerDef.label} for every translation request. Leave blank for the default F&B specialist persona.</p>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) {
  return (
    <div className="bg-zinc-50 rounded-xl px-3 py-3 border border-zinc-100">
      <div className="flex items-center gap-1.5 mb-1">{icon} <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">{label}</span></div>
      <p className="text-lg font-black text-zinc-900">{value}</p>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export function PlatformSettingsPage() {
  const [settings, setSettings] = useState<PlatformSettings>({
    platformName: 'ClickIt Platform',
    allowSignups: true,
    autoApproveNewOrganizations: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    repo().getPlatformSettings()
      .then(setSettings)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send aiApiKey if user explicitly typed a new one; strip display-only fields
      const { aiApiKeyConfigured, ...toSave } = settings;
      if (!toSave.aiApiKey || toSave.aiApiKey.includes('\u2022')) {
        delete toSave.aiApiKey;
      }
      const updated = await repo().savePlatformSettings(toSave);
      if (updated) setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const update = (patch: Partial<PlatformSettings>) =>
    setSettings(prev => ({ ...prev, ...patch }));

  const addNewsItem = () => {
    const items = [...(settings.newsItems ?? [])];
    items.push({ id: crypto.randomUUID(), title: '', date: new Date().toISOString().slice(0, 10) });
    update({ newsItems: items });
  };

  const removeNewsItem = (id: string) => {
    update({ newsItems: (settings.newsItems ?? []).filter(n => n.id !== id) });
  };

  const updateNewsItem = (id: string, patch: Partial<{ title: string; date: string }>) => {
    update({
      newsItems: (settings.newsItems ?? []).map(n =>
        n.id === id ? { ...n, ...patch } : n
      ),
    });
  };

  const [activeTab, setActiveTab] = useState<'general' | 'ai' | 'branding' | 'landing' | 'login'>('general');

  if (loading) {
    return <div className="text-center py-16 text-zinc-400">Loading platform settings…</div>;
  }

  const TABS = [
    { id: 'general' as const,  label: 'General',      icon: Palette,   color: 'text-indigo-500' },
    { id: 'ai' as const,       label: 'AI Engine',    icon: Cpu,       color: 'text-violet-500' },
    { id: 'branding' as const, label: 'Branding',     icon: ImageIcon, color: 'text-violet-500' },
    { id: 'landing' as const,  label: 'Landing Page', icon: Globe,     color: 'text-emerald-500' },
    { id: 'login' as const,    label: 'Login Page',   icon: LogIn,     color: 'text-blue-500' },
  ];

  return (
    <div className="max-w-3xl mx-auto pb-12">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-black text-zinc-900">Platform Settings</h1>
        <p className="text-sm text-zinc-500 mt-1">Configure branding, landing page content, and login experience.</p>
      </div>

      {/* ── Tab Bar ─────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 mb-6">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-bold transition-all",
                active
                  ? "bg-white text-zinc-900 shadow-sm"
                  : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
              )}
            >
              <Icon size={14} className={active ? tab.color : 'text-zinc-400'} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── Tab Content ─────────────────────────────────────────── */}

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
            <Palette size={16} className="text-indigo-500" /> General Settings
          </div>

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
              Platform Name
            </label>
            <input
              value={settings.platformName}
              onChange={e => update({ platformName: e.target.value })}
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-zinc-400 mt-1">Shown in the header, sidebar, login page, and footer. <span className="font-semibold text-zinc-500">Currently: "{settings.platformName}"</span></p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-zinc-900">Allow New Signups</p>
              <p className="text-xs text-zinc-400 mt-0.5">Allow organisations to sign up from the public landing page.</p>
            </div>
            <button
              onClick={() => update({ allowSignups: !settings.allowSignups })}
              className="transition-colors"
            >
              {settings.allowSignups
                ? <ToggleRight size={32} className="text-emerald-500" />
                : <ToggleLeft size={32} className="text-zinc-400" />
              }
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-zinc-900">Auto-approve new organizations</p>
              <p className="text-xs text-zinc-400 mt-0.5">Free-plan signups activate immediately when enabled. Paid plans still wait for payment review.</p>
            </div>
            <button
              onClick={() => update({ autoApproveNewOrganizations: !settings.autoApproveNewOrganizations })}
              className="transition-colors"
            >
              {settings.autoApproveNewOrganizations
                ? <ToggleRight size={32} className="text-emerald-500" />
                : <ToggleLeft size={32} className="text-zinc-400" />
              }
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-zinc-900 flex items-center gap-1.5">
                <Activity size={14} className="text-blue-500" /> Pulse Polling Interval
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">Global background polling interval in seconds (Default: 15s). Set to 0 to disable.</p>
            </div>
            <input
              type="number"
              min={0}
              max={300}
              value={settings.pulseIntervalSeconds ?? 15}
              onChange={e => update({ pulseIntervalSeconds: parseInt(e.target.value) || 0 })}
              className="w-20 text-center rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-1.5 text-sm font-bold text-zinc-900"
            />
          </div>

        </div>
      )}

      {/* AI Engine */}
      {activeTab === 'ai' && (
        <AiEngineTab settings={settings} update={update} />
      )}

      {/* Platform Branding */}
      {activeTab === 'branding' && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
            <ImageIcon size={16} className="text-violet-500" /> Platform Branding
          </div>

          <ImagePicker
            value={settings.logoUrl}
            onChange={url => update({ logoUrl: url })}
            purpose="logo"
            label="Platform Logo"
            hint="Displayed in the landing page header, login screen, and sidebar. Recommended: 200×50px PNG/SVG."
          />

          <div className="border-t border-zinc-100 pt-4" />

          <ImagePicker
            value={settings.faviconUrl}
            onChange={url => update({ faviconUrl: url })}
            purpose="favicon"
            label="Favicon"
            hint="Browser tab icon. Recommended: 32×32px PNG or ICO."
            accept="image/png,image/x-icon,image/svg+xml"
          />
        </div>
      )}

      {/* Landing Page CMS */}
      {activeTab === 'landing' && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
            <Globe size={16} className="text-emerald-500" /> Landing Page Content
          </div>

          <ImagePicker
            value={settings.heroImageUrl}
            onChange={url => update({ heroImageUrl: url })}
            purpose="hero"
            label="Hero Image"
            hint="Main background or showcase image for the landing page hero section."
          />

          <div className="border-t border-zinc-100 pt-4" />

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
              Hero Title
            </label>
            <input
              value={settings.heroTitle ?? ''}
              onChange={e => update({ heroTitle: e.target.value })}
              placeholder="The Modular Operating System for Logistics & Brand Operations"
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-zinc-400 mt-1">Currently showing: <span className="font-semibold text-zinc-500">"{settings.heroTitle || 'The Modular Operating System for Logistics & Brand Operations'}"</span></p>
          </div>

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
              Hero Description
            </label>
            <textarea
              value={settings.heroDescription ?? ''}
              onChange={e => update({ heroDescription: e.target.value })}
              placeholder="ClickIt is a config-driven platform for managing riders, aggregator campaigns, menus, and support tickets — all under one roof."
              rows={3}
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            />
            <p className="text-xs text-zinc-400 mt-1">Currently showing: <span className="font-semibold text-zinc-500">"{settings.heroDescription || 'ClickIt is a config-driven platform for managing riders, aggregator campaigns, menus, and support tickets — all under one roof. Start with one module, scale to many.'}"</span></p>
          </div>

          <div className="border-t border-zinc-100 pt-4" />

          {/* News / Updates */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Newspaper size={14} className="text-zinc-400" />
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
                  News &amp; Updates
                </label>
              </div>
              <button
                type="button"
                onClick={addNewsItem}
                className="flex items-center gap-1 px-2.5 py-1 bg-zinc-100 text-zinc-600 rounded-lg text-[10px] font-bold hover:bg-zinc-200 transition-colors"
              >
                <Plus size={10} /> Add Item
              </button>
            </div>
            {(!settings.newsItems || settings.newsItems.length === 0) && (
              <p className="text-xs text-zinc-400 italic">No news items yet. Add one to show on the landing page.</p>
            )}
            <div className="space-y-2">
              {(settings.newsItems ?? []).map(item => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="date"
                    value={item.date}
                    onChange={e => updateNewsItem(item.id, { date: e.target.value })}
                    className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 w-36"
                  />
                  <input
                    value={item.title}
                    onChange={e => updateNewsItem(item.id, { title: e.target.value })}
                    placeholder="Headline…"
                    className="flex-1 rounded-lg border border-zinc-200 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeNewsItem(item.id)}
                    className="text-zinc-400 hover:text-red-500 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Login Page CMS */}
      {activeTab === 'login' && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-black text-zinc-900">
            <LogIn size={16} className="text-blue-500" /> Login Page Content
          </div>

          <div>
            <label className="block text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1.5">
              Welcome Text
            </label>
            <input
              value={settings.loginWelcomeText ?? ''}
              onChange={e => update({ loginWelcomeText: e.target.value })}
              placeholder="One platform. Every operational flow."
              className="w-full rounded-xl border border-zinc-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-zinc-400 mt-1">Currently showing: <span className="font-semibold text-zinc-500">"{settings.loginWelcomeText || 'One platform. Every operational flow.'}"</span></p>
          </div>

          <div className="border-t border-zinc-100 pt-4" />

          <ImagePicker
            value={settings.loginSidePanelImageUrl}
            onChange={url => update({ loginSidePanelImageUrl: url })}
            purpose="login-panel"
            label="Side Panel Image"
            hint="Custom background image for the dark side panel on the login page. Recommended: 800×1200px."
          />
        </div>
      )}

      {/* ── Save Button (Sticky) ────────────────────────────────── */}
      <div className="sticky bottom-4 z-10 flex justify-end mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-zinc-900 text-white rounded-2xl text-sm font-bold hover:bg-zinc-800 transition-all shadow-lg disabled:opacity-50"
        >
          {saved
            ? <><CheckCircle size={15} className="text-emerald-400" /> Saved!</>
            : <><Save size={15} /> {saving ? 'Saving…' : 'Save All Settings'}</>
          }
        </button>
      </div>
    </div>
  );
}
